import { getLastBrowseItemCount } from '@/components/itemsList/browseItemCount';
import { LOCAL_STORAGE } from '@/constants';

export type MassUpdateStartCount = {
    count: number;
    capped: boolean;
};

type Translate = (
    key: string,
    interpolationValues?: Record<string, string | number>,
) => string;

let startToastShown = false;

export const isUsableItemCount = (count: unknown): count is number =>
    typeof count === 'number' && Number.isFinite(count) && count > 0;

export const formatMassUpdateCount = (value: number, languageKey?: string | null): string => {
    const locale = languageKey ? languageKey.replace(/_/g, '-') : undefined;
    return value.toLocaleString(locale);
};

export const markMassUpdateStartToastShown = (): void => {
    startToastShown = true;
};

export const clearMassUpdateStartToastShown = (): void => {
    startToastShown = false;
};

export const takeMassUpdateStartToastShown = (): boolean => {
    const shown = startToastShown;
    startToastShown = false;
    return shown;
};

/**
 * PK list → exact length. Query-based / select-all → same rule as the browse
 * hit-list (`x-items-found` vs `x-items-max`).
 */
export const resolveMassUpdateStartCount = ({
    primaryKeys,
    numberFound,
    maxNumberFound,
}: {
    primaryKeys?: string[];
    numberFound?: number | null;
    maxNumberFound?: number | null;
}): MassUpdateStartCount | null => {
    if (Array.isArray(primaryKeys)) {
        return isUsableItemCount(primaryKeys.length)
            ? { count: primaryKeys.length, capped: false }
            : null;
    }

    const browse = getLastBrowseItemCount();
    const found = browse?.numberFound ?? numberFound;
    const maxFound = browse?.maxNumberFound ?? maxNumberFound;

    if (!isUsableItemCount(found)) {
        return null;
    }

    if (isUsableItemCount(maxFound) && found > maxFound) {
        return { count: maxFound, capped: true };
    }

    return { count: found, capped: false };
};

export const getMassUpdateStartToastMessage = (
    t: Translate,
    params: MassUpdateStartCount,
    languageKey: string | null = typeof localStorage === 'undefined'
        ? null
        : localStorage.getItem(LOCAL_STORAGE.LANGUAGE_KEY),
): string => {
    if (params.capped) {
        return t('MASS_UPDATE_EVENT.STARTED_CAPPED', {
            count: formatMassUpdateCount(params.count, languageKey),
        });
    }

    if (params.count === 1) {
        return t('MASS_UPDATE_EVENT.STARTED_ONE');
    }

    return t('MASS_UPDATE_EVENT.STARTED', {
        count: formatMassUpdateCount(params.count, languageKey),
    });
};

/** RUNNING start toast: only when the dialog did not already toast, count is real, and this is the initial event. */
export const shouldShowMassUpdateStartToastFromEvent = (data: {
    totalCount?: number | null;
    finishedCount?: number | null;
}): boolean => {
    if (data.finishedCount) {
        return false;
    }

    // Peek only: consuming the flag would let a later RUNNING event toast again.
    if (startToastShown) {
        return false;
    }

    return isUsableItemCount(data.totalCount);
};
